/**
 * Master pumps on a site: which relay starts which pump, and when.
 * Split out of pages/irrigation/EspNowControllerDetail.tsx.
 */
import { useState } from 'react';
import { Settings, Zap, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { type EspNowRouter, type MasterPump, usableRelays } from '../../../services/espnowIrrigation';

// ─── Master Pump Config ───────────────────────────────────────────────────────

export function MasterPumpConfig({
  masterPumps,
  routers,
  onAdd,
  onUpdate,
  onDelete,
}: {
  masterPumps: MasterPump[];
  routers: EspNowRouter[];
  onAdd: (routerMac: string, relayIndex: number, label: string) => void;
  onUpdate: (pumpId: string, data: { router_mac?: string; relay_index?: number; label?: string | null }) => void;
  onDelete: (pumpId: string) => void;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [addRouterMac, setAddRouterMac] = useState(routers[0]?.router_mac ?? '');
  const [addRelayIndex, setAddRelayIndex] = useState(0);
  const [addLabel, setAddLabel] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');

  function handleAdd() {
    if (!addRouterMac) return;
    onAdd(addRouterMac, addRelayIndex, addLabel.trim());
    setShowAdd(false);
    setAddLabel('');
    setAddRelayIndex(0);
  }

  function startEdit(pump: MasterPump) {
    setEditingId(pump.id);
    setEditLabel(pump.label ?? '');
  }

  function saveEdit(pump: MasterPump) {
    onUpdate(pump.id, { label: editLabel.trim() || null });
    setEditingId(null);
  }

  const selectedAddRouter = routers.find(r => r.router_mac === addRouterMac);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" /> Master Pumps / Valves
          </span>
          <button
            onClick={() => { setShowAdd(f => !f); setAddRouterMac(routers[0]?.router_mac ?? ''); }}
            className="flex items-center gap-1 px-2.5 py-1 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg"
          >
            <Plus className="w-3.5 h-3.5" /> Add Pump
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <p className="text-xs text-gray-400">
          Master pumps run automatically when any relay assigned to them is active.
          Each zone relay can choose which pump supplies it.
        </p>

        {/* Add form */}
        {showAdd && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-3">
            <p className="text-xs font-medium text-amber-800">New master pump / valve</p>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Router</label>
                <select
                  value={addRouterMac}
                  onChange={e => {
                    setAddRouterMac(e.target.value);
                    // Its first relay IN USE, not its relay 0 — on a board
                    // wired from channel 2 up, relay 0 is not an option the
                    // list below offers, and a select whose value is not among
                    // its options shows blank.
                    const next = routers.find(r => r.router_mac === e.target.value);
                    setAddRelayIndex(usableRelays(next)[0] ?? 0);
                  }}
                  className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  {routers.map(r => (
                    <option key={r.router_mac} value={r.router_mac}>
                      {r.name || r.router_mac.slice(-8)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Relay</label>
                <select
                  value={addRelayIndex}
                  onChange={e => setAddRelayIndex(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                >
                  {usableRelays(selectedAddRouter).map(i => {
                    const lbl = selectedAddRouter?.relay_labels?.[String(i)];
                    return <option key={i} value={i}>{lbl || `Relay ${i}`}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Label</label>
                <input
                  type="text"
                  maxLength={64}
                  placeholder="e.g. Main Pump"
                  value={addLabel}
                  onChange={e => setAddLabel(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 w-36"
                />
              </div>
              <div className="flex gap-2">
                <button onClick={handleAdd} disabled={!addRouterMac} className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded-lg font-medium disabled:opacity-50">
                  Add
                </button>
                <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 border border-gray-300 text-xs rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pump list */}
        {masterPumps.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No master pumps configured.</p>
        ) : (
          masterPumps.map(pump => {
            const pumpRouter = routers.find(r => r.router_mac === pump.router_mac);
            const routerLabel = pumpRouter?.name || pump.router_mac.slice(-8);
            const relayLabel = pumpRouter?.relay_labels?.[String(pump.relay_index)] || `Relay ${pump.relay_index}`;
            return (
              <div key={pump.id} className="flex items-center gap-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <Zap className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  {editingId === pump.id ? (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        maxLength={64}
                        value={editLabel}
                        onChange={e => setEditLabel(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') saveEdit(pump); if (e.key === 'Escape') setEditingId(null); }}
                        className="border border-gray-300 rounded px-2 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-amber-400 w-36"
                      />
                      <button onClick={() => saveEdit(pump)} className="text-xs text-amber-600 hover:underline">Save</button>
                      <button onClick={() => setEditingId(null)} className="text-xs text-gray-400 hover:underline">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm font-medium text-amber-800">
                        {pump.label || `${routerLabel} / ${relayLabel}`}
                      </span>
                      <span className="text-xs text-gray-400">{routerLabel} · {relayLabel}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(pump)} className="p-1 text-gray-400 hover:text-amber-600 rounded" title="Edit label">
                    <Settings className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onDelete(pump.id)} className="p-1 text-gray-400 hover:text-red-500 rounded" title="Remove pump">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
